const RIBBON_MAX_POSTS = 12
// Below this the loop shows the same card twice on screen, which reads as a bug.
const RIBBON_MIN_POSTS = 4

/** Fit the whole post inside the stage without changing its proportions. */
export function ribbonCardSize(ratio: number, maxWidth: number, maxHeight: number) {
  const aspect = Number.isFinite(ratio) && ratio > 0 ? ratio : 1
  const height = Math.min(maxHeight, maxWidth / aspect)
  return { width: height * aspect, height }
}

export function ribbonTrack(widths: number[], gap: number) {
  const centres = [0]
  for (let index = 1; index < widths.length; index++) {
    centres.push(centres[index - 1] + (widths[index - 1] + widths[index]) / 2 + gap)
  }
  return { centres, length: widths.reduce((sum, width) => sum + width + gap, 0) }
}

/** Scroll is measured in cards; interpolate between their differently spaced centres. */
export function ribbonTrackX(index: number, scroll: number, track: ReturnType<typeof ribbonTrack>) {
  const count = track.centres.length
  const whole = Math.floor(scroll)
  const current = ((whole % count) + count) % count
  const start = track.centres[current]
  const end = current + 1 < count ? track.centres[current + 1] : track.length
  const offset = start + (end - start) * (scroll - whole)
  const raw = track.centres[index] - offset
  return raw - Math.round(raw / track.length) * track.length
}

export type RibbonCapabilities = {
  reducedMotion: boolean
  webgl: boolean
  saveData: boolean
  deviceMemory?: number
  cores?: number
}

export function ribbonPosts<T>(posts: T[]): T[] {
  return posts.slice(0, RIBBON_MAX_POSTS)
}

export function shouldUseRibbon(caps: RibbonCapabilities, postCount: number): boolean {
  if (postCount < RIBBON_MIN_POSTS) return false
  if (caps.reducedMotion || !caps.webgl || caps.saveData) return false
  if (caps.deviceMemory !== undefined && caps.deviceMemory <= 2) return false
  if (caps.cores !== undefined && caps.cores <= 2) return false
  return true
}

// 640 is the nearest width the image pipeline allows above the biggest card.
export function ribbonImageUrl(imageUrl: string): string {
  return `/_next/image?url=${encodeURIComponent(imageUrl)}&w=640&q=75`
}

type Press = { dx: number; dy: number; ms: number }

// A drag that ends where it started is a tap; anything longer or further is a swipe
// and must not open the post under the finger.
export function isTap({ dx, dy, ms }: Press): boolean {
  return Math.hypot(dx, dy) < 8 && ms < 500
}

export function activeIndex(scroll: number, step: number, count: number): number {
  const i = Math.round(scroll / step) % count
  return i < 0 ? i + count : i
}

// Position of card `index` on the endless strip, wrapped to lie within half a
// loop either side of the centre.
export function wrappedX(index: number, scroll: number, step: number, count: number): number {
  const loop = step * count
  const raw = index * step - scroll
  return raw - Math.round(raw / loop) * loop
}

/** Choose a fresh opener from the six newest posts, without favouring an old archive item. */
export function randomRecentPostIndex<T extends { href: string }>(
  posts: T[],
  previousHref = '',
  random = Math.random()
): number {
  const recent = posts.slice(0, 6).map((post, index) => ({ href: post.href, index }))
  const choices = recent.length > 1 ? recent.filter((post) => post.href !== previousHref) : recent
  return choices[Math.min(Math.floor(random * choices.length), choices.length - 1)]?.index ?? 0
}
